/**
 * Fase A — Webshop Multi-step Checkout Wizard.
 *
 * 4 stappen: Contact → Bezorgadres → Factuuradres → Controle & Betalen.
 * Winkelwagen blijft buiten de dialog (linker kolom op WebshopPage), zodat
 * de gebruiker altijd zicht op zijn items houdt. De wizard zit IN dialog,
 * Shopify-stijl breadcrumbs bovenaan voor stap-overzicht.
 *
 * NIET in deze sprint:
 *   - klantaccount aanmaken
 *   - inlog/wachtwoord
 *   - retour
 *   - Stripe refunds
 *   - dynamische verzendkosten
 *
 * Backend contract (Fase A):
 *   POST /api/webshop/checkout/create-session  ←  exact deze payload-shape
 *   - customer.first_name / last_name / email / phone (verplicht)
 *   - customer.whatsapp / company_name / notes (optioneel)
 *   - shipping_address.{street,house_number,postal_code,city,country} (verplicht)
 *   - shipping_address.{house_number_addition,delivery_note} (optioneel)
 *   - billing_address.same_as_shipping  →  spiegel shipping
 *   - billing_address met aparte velden inclusief vat_number/kvk wanneer apart
 *   - terms_accepted=true, privacy_accepted=true (verplicht)
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, ChevronRight, ChevronLeft, Lock, Truck, Tag, User as UserIcon } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

import { useCart } from "../../context/CartContext";
import { useShopAuth, shopApi } from "../../context/ShopAuthContext";
import { createWebshopCheckoutSession } from "../../lib/api";
import { usePostcodeLookup, postcodeLookupLabel } from "../../lib/postcodeLookup";

const fmtEur = (val) => `€ ${Number(val || 0).toFixed(2)}`;

const STEPS = [
  { key: "contact", label: "Contactgegevens" },
  { key: "shipping", label: "Bezorgadres" },
  { key: "billing", label: "Factuuradres" },
  { key: "shipping_method", label: "Verzending" },
  { key: "review", label: "Controle & betalen" },
];

const EMPTY_FORM = {
  customer: {
    first_name: "", last_name: "", email: "", phone: "",
    whatsapp: "", company_name: "", notes: "",
  },
  shipping: {
    street: "", house_number: "", house_number_addition: "",
    postal_code: "", city: "", country: "Nederland", delivery_note: "",
  },
  billing: {
    same_as_shipping: true,
    company_name: "", vat_number: "", chamber_of_commerce_number: "",
    street: "", house_number: "", house_number_addition: "",
    postal_code: "", city: "", country: "Nederland",
  },
  terms_accepted: false,
  privacy_accepted: false,
};

// ─────────────── Stap-indicator ───────────────
function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-1 sm:gap-2 mb-4 overflow-x-auto" data-testid="checkout-step-indicator">
      {STEPS.map((s, idx) => {
        const active = STEPS.findIndex((x) => x.key === current) === idx;
        const done = STEPS.findIndex((x) => x.key === current) > idx;
        return (
          <React.Fragment key={s.key}>
            <div
              data-testid={`checkout-step-${s.key}`}
              data-active={active || undefined}
              data-done={done || undefined}
              className={`flex items-center gap-1.5 px-2 py-1 text-[11px] sm:text-xs whitespace-nowrap border ${
                active
                  ? "bg-[#082c50] text-white border-[#082c50]"
                  : done
                  ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              {done ? <CheckCircle2 className="w-3 h-3" /> : <span className="font-bold">{idx + 1}</span>}
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{idx + 1}</span>
            </div>
            {idx < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────── Adres-veldenset (herbruikt voor shipping + billing) ───────────────
function AddressFieldset({ value, onChange, testidPrefix, disabled }) {
  const { status, result } = usePostcodeLookup({
    postal_code: value.postal_code,
    house_number: value.house_number,
    country: value.country,
    enabled: !disabled,
    variant: "public",
  });

  // Auto-fill helper: vul street/city alleen als ze nog leeg zijn,
  // zodat handmatige invoer niet wordt overschreven.
  React.useEffect(() => {
    if (status !== "found" || !result) return;
    const patch = {};
    if (!value.street && result.street) patch.street = result.street;
    if (!value.city && result.city) patch.city = result.city;
    if (!value.country && result.country) patch.country = result.country;
    if (Object.keys(patch).length) onChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, result]);

  const info = postcodeLookupLabel(status, result);
  const Icon = status === "loading" ? Loader2 : status === "found" ? CheckCircle2 : AlertCircle;

  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5">
      <div className="md:col-span-2">
        <Label className="text-[11px] uppercase font-bold">Postcode *</Label>
        <Input
          data-testid={`${testidPrefix}-postal-code`}
          value={value.postal_code}
          onChange={(e) => onChange({ postal_code: e.target.value })}
          placeholder="3194 AG"
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[11px] uppercase font-bold">Huisnummer *</Label>
        <Input
          data-testid={`${testidPrefix}-house-number`}
          value={value.house_number}
          onChange={(e) => onChange({ house_number: e.target.value })}
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[11px] uppercase font-bold">Toevoeging</Label>
        <Input
          data-testid={`${testidPrefix}-house-number-addition`}
          value={value.house_number_addition}
          onChange={(e) => onChange({ house_number_addition: e.target.value })}
          placeholder="A"
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      <div className="md:col-span-3">
        <Label className="text-[11px] uppercase font-bold">Straatnaam *</Label>
        <Input
          data-testid={`${testidPrefix}-street`}
          value={value.street}
          onChange={(e) => onChange({ street: e.target.value })}
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[11px] uppercase font-bold">Stad *</Label>
        <Input
          data-testid={`${testidPrefix}-city`}
          value={value.city}
          onChange={(e) => onChange({ city: e.target.value })}
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase font-bold">Land *</Label>
        <Input
          data-testid={`${testidPrefix}-country`}
          value={value.country}
          onChange={(e) => onChange({ country: e.target.value })}
          disabled={disabled}
          className="rounded-xl mt-1"
        />
      </div>
      {status !== "idle" && (
        <div
          data-testid={`${testidPrefix}-lookup-status`}
          data-status={status}
          role="status"
          aria-live="polite"
          className={`md:col-span-6 flex items-center gap-1.5 text-xs px-2.5 py-1.5 border ${
            info.color === "green"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : info.color === "amber"
              ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-gray-50 border-gray-200 text-gray-700"
          }`}
        >
          <Icon className={`w-3.5 h-3.5 ${status === "loading" ? "animate-spin" : ""}`} />
          <span>{info.text}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────── De wizard zelf ───────────────
export default function CheckoutWizard({ open, onOpenChange }) {
  const { items, totals } = useCart();
  const { customer: shopCustomer } = useShopAuth();
  const [step, setStep] = useState("contact");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Fase B state
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [shippingMethods, setShippingMethods] = useState([]);
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState(null);  // { valid, discount_cents, reason }
  const [discountChecking, setDiscountChecking] = useState(false);

  // Prefill bij ingelogde shopaccount klant
  React.useEffect(() => {
    if (!open || !shopCustomer) return;
    setForm((f) => ({
      ...f,
      customer: {
        ...f.customer,
        first_name: f.customer.first_name || shopCustomer.first_name || "",
        last_name: f.customer.last_name || shopCustomer.last_name || "",
        email: f.customer.email || shopCustomer.email || "",
        phone: f.customer.phone || shopCustomer.phone || "",
        whatsapp: f.customer.whatsapp || shopCustomer.whatsapp || "",
      },
      shipping: shopCustomer.default_shipping_address ? {
        ...f.shipping,
        ...shopCustomer.default_shipping_address,
        delivery_note: f.shipping.delivery_note,
      } : f.shipping,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shopCustomer]);

  // Load shipping methods bij stap = shipping_method
  React.useEffect(() => {
    if (step !== "shipping_method") return;
    const subtotal_cents = Math.round((totals?.subtotal || 0) * 100);
    const country = (form.shipping.country || "").trim();
    shopApi.get(`/webshop/shipping-methods`, { params: { country, order_subtotal_cents: subtotal_cents } })
      .then((r) => {
        setShippingMethods(r.data || []);
        // Auto-pick first method als nog niets gekozen
        if (!shippingMethodId && r.data?.length > 0) setShippingMethodId(r.data[0].id);
      })
      .catch(() => setShippingMethods([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const selectedMethod = useMemo(
    () => shippingMethods.find((m) => m.id === shippingMethodId) || null,
    [shippingMethods, shippingMethodId],
  );
  const shippingCents = selectedMethod ? Number(selectedMethod.amount_cents || 0) : 0;
  const discountCents = discountResult?.valid ? Number(discountResult.discount_cents || 0) : 0;
  const subtotalCents = Math.round((totals?.subtotal || 0) * 100);
  const grandTotalCents = Math.max(0, subtotalCents + shippingCents - discountCents);

  const checkDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountResult(null);
      return;
    }
    setDiscountChecking(true);
    try {
      const r = await shopApi.get("/webshop/discount-codes/validate", {
        params: { code: discountCode.trim(), subtotal_cents: subtotalCents },
      });
      setDiscountResult(r.data);
      if (!r.data?.valid) toast.error(r.data?.reason || "Ongeldige code");
      else toast.success(`Korting toegepast: ${r.data.code}`);
    } catch (err) {
      setDiscountResult({ valid: false, discount_cents: 0, reason: "Validatie mislukte" });
    } finally {
      setDiscountChecking(false);
    }
  };

  const patch = (section, fields) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], ...fields } }));

  const validateContact = () => {
    const e = {};
    if (!form.customer.first_name.trim()) e.first_name = "Voornaam is verplicht";
    if (!form.customer.last_name.trim()) e.last_name = "Achternaam is verplicht";
    if (!form.customer.email.trim()) e.email = "E-mail is verplicht";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer.email)) e.email = "Vul een geldig e-mailadres in";
    if (!form.customer.phone.trim()) e.phone = "Telefoon is verplicht";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateShipping = () => {
    const e = {};
    const s = form.shipping;
    if (!s.street.trim()) e.street = "Straatnaam is verplicht";
    if (!s.house_number.trim()) e.house_number = "Huisnummer is verplicht";
    if (!s.postal_code.trim()) e.postal_code = "Postcode is verplicht";
    if (!s.city.trim()) e.city = "Stad is verplicht";
    if (!s.country.trim()) e.country = "Land is verplicht";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateBilling = () => {
    if (form.billing.same_as_shipping) { setErrors({}); return true; }
    const e = {};
    const b = form.billing;
    if (!b.street.trim()) e.b_street = "Straatnaam is verplicht";
    if (!b.house_number.trim()) e.b_house_number = "Huisnummer is verplicht";
    if (!b.postal_code.trim()) e.b_postal_code = "Postcode is verplicht";
    if (!b.city.trim()) e.b_city = "Stad is verplicht";
    if (!b.country.trim()) e.b_country = "Land is verplicht";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateReview = () => {
    const e = {};
    if (!form.terms_accepted) e.terms = "U moet akkoord gaan met de algemene voorwaarden.";
    if (!form.privacy_accepted) e.privacy = "U moet akkoord gaan met het privacybeleid.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (step === "contact" && !validateContact()) return;
    if (step === "shipping" && !validateShipping()) return;
    if (step === "billing" && !validateBilling()) return;
    const idx = STEPS.findIndex((s) => s.key === step);
    setStep(STEPS[Math.min(idx + 1, STEPS.length - 1)].key);
    setErrors({});
  };

  const goPrev = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    setStep(STEPS[Math.max(idx - 1, 0)].key);
    setErrors({});
  };

  const submitOrder = async () => {
    if (!items.length) {
      toast.error("Uw winkelwagen is leeg");
      return;
    }
    if (!validateReview()) return;
    if (createAccount && !shopCustomer) {
      if (accountPassword.length < 8 || !/[A-Za-z]/.test(accountPassword) || !/\d/.test(accountPassword)) {
        toast.error("Wachtwoord moet minimaal 8 tekens hebben met minimaal 1 letter en 1 cijfer.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Optioneel: account aanmaken vóór checkout zodat shop_customer_id wordt gelinkt
      if (createAccount && !shopCustomer) {
        try {
          await shopApi.post("/shop/auth/register", {
            first_name: form.customer.first_name.trim(),
            last_name: form.customer.last_name.trim(),
            email: form.customer.email.trim().toLowerCase(),
            phone: form.customer.phone.trim(),
            whatsapp: form.customer.whatsapp.trim() || null,
            password: accountPassword,
          });
          toast.success("Account aangemaakt — verificatielink verstuurd naar uw e-mail.");
        } catch (regErr) {
          const detail = regErr?.response?.data?.detail || "Account aanmaken mislukt";
          toast.error(typeof detail === "string" ? detail : "Account aanmaken mislukt");
          setSubmitting(false);
          return;
        }
      }
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          variation_id: i.variation_id || null,
          quantity: i.quantity,
        })),
        customer: {
          first_name: form.customer.first_name.trim(),
          last_name: form.customer.last_name.trim(),
          email: form.customer.email.trim().toLowerCase(),
          phone: form.customer.phone.trim(),
          whatsapp: form.customer.whatsapp.trim() || null,
          company_name: form.customer.company_name.trim() || null,
          notes: form.customer.notes.trim() || null,
        },
        shipping_address: {
          street: form.shipping.street.trim(),
          house_number: form.shipping.house_number.trim(),
          house_number_addition: form.shipping.house_number_addition.trim() || null,
          postal_code: form.shipping.postal_code.trim(),
          city: form.shipping.city.trim(),
          country: form.shipping.country.trim(),
          delivery_note: form.shipping.delivery_note.trim() || null,
        },
        billing_address: form.billing.same_as_shipping
          ? { same_as_shipping: true,
              company_name: form.billing.company_name.trim() || null,
              vat_number: form.billing.vat_number.trim() || null,
              chamber_of_commerce_number: form.billing.chamber_of_commerce_number.trim() || null }
          : {
              same_as_shipping: false,
              company_name: form.billing.company_name.trim() || null,
              vat_number: form.billing.vat_number.trim() || null,
              chamber_of_commerce_number: form.billing.chamber_of_commerce_number.trim() || null,
              street: form.billing.street.trim(),
              house_number: form.billing.house_number.trim(),
              house_number_addition: form.billing.house_number_addition.trim() || null,
              postal_code: form.billing.postal_code.trim(),
              city: form.billing.city.trim(),
              country: form.billing.country.trim(),
            },
        shipping_method_id: shippingMethodId || null,
        discount_code: discountResult?.valid ? discountResult.code : null,
        create_account: createAccount && !shopCustomer,
        terms_accepted: form.terms_accepted,
        privacy_accepted: form.privacy_accepted,
        origin_url: window.location.origin,
      };
      const res = await createWebshopCheckoutSession(payload);
      try { localStorage.setItem("cargo_last_session_id", res.session_id || ""); } catch (_) {}
      window.location.href = res.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Checkout kon niet worden gestart";
      toast.error(typeof detail === "string" ? detail : "Checkout kon niet worden gestart");
      setSubmitting(false);
    }
  };

  // Reset form bij sluiten zodat een tweede checkout schoon start.
  React.useEffect(() => {
    if (!open) {
      setStep("contact");
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  const reviewAddress = form.billing.same_as_shipping ? form.shipping : form.billing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="checkout-wizard">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-2xl text-[#082c50]">
            Afrekenen
          </DialogTitle>
          <DialogDescription>
            Vul uw gegevens in. U wordt veilig doorgestuurd naar Stripe nadat alles is gecontroleerd.
            Totaal: <strong>{fmtEur(totals.subtotal)}</strong>.
          </DialogDescription>
        </DialogHeader>

        <StepIndicator current={step} />

        {/* STAP 1 — CONTACT */}
        {step === "contact" && (
          <div className="space-y-3" data-testid="checkout-section-contact">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">Voornaam *</Label>
                <Input data-testid="checkout-first-name" value={form.customer.first_name}
                  onChange={(e) => patch("customer", { first_name: e.target.value })} className="rounded-xl mt-1" />
                {errors.first_name && <p className="text-xs text-red-600 mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <Label className="text-[11px] uppercase font-bold">Achternaam *</Label>
                <Input data-testid="checkout-last-name" value={form.customer.last_name}
                  onChange={(e) => patch("customer", { last_name: e.target.value })} className="rounded-xl mt-1" />
                {errors.last_name && <p className="text-xs text-red-600 mt-1">{errors.last_name}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">E-mail *</Label>
                <Input data-testid="checkout-email" type="email" value={form.customer.email}
                  onChange={(e) => patch("customer", { email: e.target.value })} className="rounded-xl mt-1" />
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label className="text-[11px] uppercase font-bold">Telefoon *</Label>
                <Input data-testid="checkout-phone" value={form.customer.phone}
                  onChange={(e) => patch("customer", { phone: e.target.value })} className="rounded-xl mt-1" />
                {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">WhatsApp (optioneel)</Label>
                <Input data-testid="checkout-whatsapp" value={form.customer.whatsapp}
                  onChange={(e) => patch("customer", { whatsapp: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-bold">Bedrijfsnaam (optioneel)</Label>
                <Input data-testid="checkout-company" value={form.customer.company_name}
                  onChange={(e) => patch("customer", { company_name: e.target.value })} className="rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold">Opmerkingen (optioneel)</Label>
              <Textarea data-testid="checkout-notes" rows={2} value={form.customer.notes}
                onChange={(e) => patch("customer", { notes: e.target.value })} className="rounded-xl mt-1" />
            </div>

            {/* Phase B — optioneel shopaccount aanmaken */}
            {shopCustomer ? (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 flex items-center gap-2" data-testid="checkout-shop-logged-in">
                <UserIcon className="w-4 h-4 text-[#1a5193]" />
                <p className="text-xs text-blue-900">
                  Ingelogd als <strong>{shopCustomer.email}</strong> — uw gegevens zijn voorgevuld.
                </p>
              </div>
            ) : (
              <>
                {/* Phase B-Polish — duidelijke login-prompt voor gasten */}
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200" data-testid="checkout-shop-login-prompt">
                  <p className="text-xs text-amber-900">
                    <strong>Heeft u al een shopaccount?</strong> Log in om uw gegevens automatisch te laten invullen en uw bestellingen op één plek te bewaren.
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <a
                      href={`/shop-account/login?returnTo=${encodeURIComponent("/webshop")}`}
                      className="text-xs font-bold text-[#082c50] underline"
                      data-testid="checkout-shop-login-link"
                    >Log in</a>
                    <span className="text-amber-700 text-xs">of</span>
                    <a
                      href={`/shop-account/register?returnTo=${encodeURIComponent("/webshop")}`}
                      className="text-xs font-bold text-[#1a5193] underline"
                      data-testid="checkout-shop-register-link"
                    >maak een account aan</a>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 space-y-2" data-testid="checkout-shop-create-account-block">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox data-testid="checkout-create-account" checked={createAccount}
                      onCheckedChange={(v) => setCreateAccount(Boolean(v))} className="mt-0.5" />
                    <span className="text-xs">Maak direct een account aan voor toekomstige bestellingen — u ontvangt een verificatiemail. <strong>Winkelwagen blijft behouden.</strong></span>
                  </label>
                  {createAccount && (
                    <div data-testid="checkout-create-account-password">
                      <Label className="text-[11px] uppercase font-bold">Kies een wachtwoord *</Label>
                      <Input type="password" data-testid="checkout-create-account-pw" value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)} className="rounded-xl mt-1" />
                      <p className="text-[10px] text-gray-500 mt-1">Minimaal 8 tekens, met minimaal 1 letter en 1 cijfer.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* STAP 2 — BEZORGADRES */}
        {step === "shipping" && (
          <div className="space-y-3" data-testid="checkout-section-shipping">
            <p className="text-xs text-gray-500">
              Voor Nederlandse adressen: vul postcode + huisnummer in voor automatisch ophalen van straat en plaats.
            </p>
            <AddressFieldset
              value={form.shipping}
              onChange={(p) => patch("shipping", p)}
              testidPrefix="checkout-ship"
            />
            {Object.keys(errors).length > 0 && (
              <div className="text-xs text-red-600" data-testid="checkout-ship-errors">
                {Object.values(errors).map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase font-bold">Bezorginstructie (optioneel)</Label>
              <Textarea data-testid="checkout-delivery-note" rows={2} value={form.shipping.delivery_note}
                onChange={(e) => patch("shipping", { delivery_note: e.target.value })}
                placeholder="Bijv. bezorgen bij buren nr. 90, of bellen vóór bezorging."
                className="rounded-xl mt-1" />
            </div>
          </div>
        )}

        {/* STAP 3 — FACTUURADRES */}
        {step === "billing" && (
          <div className="space-y-3" data-testid="checkout-section-billing">
            <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 bg-gray-50">
              <Checkbox
                data-testid="checkout-billing-same"
                checked={form.billing.same_as_shipping}
                onCheckedChange={(v) => patch("billing", { same_as_shipping: Boolean(v) })}
              />
              <span className="text-sm font-bold">Factuuradres is hetzelfde als bezorgadres</span>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">Bedrijfsnaam (optioneel)</Label>
                <Input data-testid="checkout-bill-company" value={form.billing.company_name}
                  onChange={(e) => patch("billing", { company_name: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-bold">BTW-nummer (optioneel)</Label>
                <Input data-testid="checkout-bill-vat" value={form.billing.vat_number}
                  onChange={(e) => patch("billing", { vat_number: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase font-bold">KvK-nummer (optioneel)</Label>
                <Input data-testid="checkout-bill-kvk" value={form.billing.chamber_of_commerce_number}
                  onChange={(e) => patch("billing", { chamber_of_commerce_number: e.target.value })} className="rounded-xl mt-1" />
              </div>
            </div>

            {!form.billing.same_as_shipping && (
              <>
                <p className="text-xs text-gray-500 pt-2">Vul het afwijkende factuuradres in:</p>
                <AddressFieldset
                  value={form.billing}
                  onChange={(p) => patch("billing", p)}
                  testidPrefix="checkout-bill"
                />
                {Object.keys(errors).length > 0 && (
                  <div className="text-xs text-red-600" data-testid="checkout-bill-errors">
                    {Object.values(errors).map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* STAP 4 — VERZENDING + KORTING */}
        {step === "shipping_method" && (
          <div className="space-y-4" data-testid="checkout-section-shipping-method">
            <div>
              <h4 className="font-chivo font-black uppercase text-xs text-[#082c50] mb-2 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Verzendmethode</h4>
              {shippingMethods.length === 0 ? (
                <div className="text-xs text-gray-500 italic p-3 border border-dashed border-gray-300" data-testid="checkout-shipping-empty">
                  Geen verzendmethode beschikbaar voor dit land of orderbedrag. Neem contact op of kies een ander adres.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="checkout-shipping-methods">
                  {shippingMethods.map((m) => (
                    <li key={m.id}>
                      <label className={`flex items-start gap-3 p-3 border cursor-pointer ${shippingMethodId === m.id ? "border-[#082c50] bg-blue-50" : "border-gray-200"}`} data-testid={`checkout-ship-method-${m.id}`}>
                        <input type="radio" name="shipping_method" checked={shippingMethodId === m.id} onChange={() => setShippingMethodId(m.id)} className="mt-0.5" />
                        <div className="flex-1">
                          <div className="font-bold text-sm">{m.name}</div>
                          {m.description && <div className="text-xs text-gray-600">{m.description}</div>}
                        </div>
                        <div className="text-right text-sm font-bold">
                          {m.is_free ? <span className="text-emerald-700">Gratis</span> : fmtEur(m.amount_cents/100)}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="font-chivo font-black uppercase text-xs text-[#082c50] mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Kortingscode</h4>
              <div className="flex gap-2">
                <Input
                  data-testid="checkout-discount-input"
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountResult(null); }}
                  placeholder="Bijv. WELCOME10"
                  className="rounded-xl uppercase"
                />
                <Button type="button" onClick={checkDiscount} disabled={discountChecking || !discountCode.trim()}
                  className="rounded-xl text-xs font-chivo font-bold uppercase bg-[#1a5193] hover:bg-[#194d8d]" data-testid="checkout-discount-apply">
                  {discountChecking ? <Loader2 className="w-3 h-3 animate-spin" /> : "Toepassen"}
                </Button>
              </div>
              {discountResult && (
                <div className={`mt-2 text-xs p-2 border ${discountResult.valid ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                  data-testid="checkout-discount-result" data-valid={discountResult.valid ? "true" : "false"}>
                  {discountResult.valid
                    ? `Korting toegepast: ${discountResult.code} — ${fmtEur(discountResult.discount_cents/100)} afgetrokken.`
                    : discountResult.reason}
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 flex justify-between border-t border-gray-200 pt-2">
              <span>Subtotaal</span><span>{fmtEur(subtotalCents/100)}</span>
            </div>
            {discountCents > 0 && (
              <div className="text-xs text-emerald-700 flex justify-between">
                <span>Korting</span><span>− {fmtEur(discountCents/100)}</span>
              </div>
            )}
            {shippingCents > 0 && (
              <div className="text-xs text-gray-700 flex justify-between">
                <span>Verzending</span><span>{fmtEur(shippingCents/100)}</span>
              </div>
            )}
            <div className="text-base font-chivo font-black text-[#082c50] flex justify-between border-t border-gray-300 pt-2">
              <span>Totaal te betalen</span><span data-testid="checkout-ship-total">{fmtEur(grandTotalCents/100)}</span>
            </div>
          </div>
        )}

        {/* STAP 5 — REVIEW & BETALEN */}
        {step === "review" && (
          <div className="space-y-4" data-testid="checkout-section-review">
            <ReviewBlock title="Contactgegevens">
              <div>{form.customer.first_name} {form.customer.last_name}</div>
              <div className="text-gray-600">{form.customer.email}</div>
              <div className="text-gray-600">{form.customer.phone}</div>
              {form.customer.whatsapp && <div className="text-gray-600">WhatsApp: {form.customer.whatsapp}</div>}
              {form.customer.company_name && <div className="text-gray-600">{form.customer.company_name}</div>}
            </ReviewBlock>
            <ReviewBlock title="Bezorgadres">
              <div>{form.shipping.street} {form.shipping.house_number}{form.shipping.house_number_addition ? ` ${form.shipping.house_number_addition}` : ""}</div>
              <div>{form.shipping.postal_code} {form.shipping.city}</div>
              <div>{form.shipping.country}</div>
              {form.shipping.delivery_note && <div className="text-gray-600 italic">Instructie: {form.shipping.delivery_note}</div>}
            </ReviewBlock>
            <ReviewBlock title="Factuuradres">
              {form.billing.same_as_shipping ? (
                <div className="text-gray-600 italic">Hetzelfde als bezorgadres</div>
              ) : (
                <>
                  {reviewAddress.company_name && <div>{form.billing.company_name}</div>}
                  <div>{reviewAddress.street} {reviewAddress.house_number}{reviewAddress.house_number_addition ? ` ${reviewAddress.house_number_addition}` : ""}</div>
                  <div>{reviewAddress.postal_code} {reviewAddress.city}</div>
                  <div>{reviewAddress.country}</div>
                </>
              )}
              {form.billing.vat_number && <div className="text-gray-600">BTW: {form.billing.vat_number}</div>}
              {form.billing.chamber_of_commerce_number && <div className="text-gray-600">KvK: {form.billing.chamber_of_commerce_number}</div>}
            </ReviewBlock>

            <ReviewBlock title="Uw bestelling">
              <ul className="divide-y divide-gray-200 text-xs">
                {items.map((it) => (
                  <li key={`${it.product_id}::${it.variation_id || ""}`} className="py-1.5 flex justify-between">
                    <span>{it.name}{it.variation_name ? ` — ${it.variation_name}` : ""} × {it.quantity}</span>
                    <span className="font-bold">{fmtEur((it.price || 0) * it.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-xs">
                <div className="flex justify-between"><span>Subtotaal</span><span>{fmtEur(subtotalCents/100)}</span></div>
                {discountResult?.valid && (
                  <div className="flex justify-between text-emerald-700" data-testid="checkout-review-discount">
                    <span>Korting ({discountResult.code})</span>
                    <span>− {fmtEur(discountCents/100)}</span>
                  </div>
                )}
                {selectedMethod && (
                  <div className="flex justify-between" data-testid="checkout-review-shipping">
                    <span>Verzending — {selectedMethod.name}</span>
                    <span>{shippingCents > 0 ? fmtEur(shippingCents/100) : <span className="text-emerald-700 font-bold">Gratis</span>}</span>
                  </div>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-base font-chivo font-black text-[#082c50]">
                <span>Totaal incl. BTW</span>
                <span data-testid="checkout-review-total">{fmtEur(grandTotalCents/100)}</span>
              </div>
            </ReviewBlock>

            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  data-testid="checkout-terms-accepted"
                  checked={form.terms_accepted}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, terms_accepted: Boolean(v) }))}
                  className="mt-0.5"
                />
                <span className="text-xs text-gray-700">
                  Ik ga akkoord met de <a href="/voorwaarden" target="_blank" rel="noreferrer" className="underline text-[#082c50]">algemene voorwaarden</a> van 5999Cargo. *
                </span>
              </label>
              {errors.terms && <p className="text-xs text-red-600 ml-6" data-testid="checkout-terms-error">{errors.terms}</p>}
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  data-testid="checkout-privacy-accepted"
                  checked={form.privacy_accepted}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, privacy_accepted: Boolean(v) }))}
                  className="mt-0.5"
                />
                <span className="text-xs text-gray-700">
                  Ik ga akkoord met het <a href="/privacybeleid" target="_blank" rel="noreferrer" className="underline text-[#082c50]">privacybeleid</a> en de verwerking van mijn klantgegevens. *
                </span>
              </label>
              {errors.privacy && <p className="text-xs text-red-600 ml-6" data-testid="checkout-privacy-error">{errors.privacy}</p>}
            </div>

            <div className="text-xs text-gray-500 flex items-center gap-1 justify-center pt-2">
              <Lock className="w-3 h-3" />
              Veilig betalen via Stripe — uw gegevens worden eerst bij 5999Cargo opgeslagen.
            </div>
          </div>
        )}

        {/* Navigatie */}
        <div className="flex items-center justify-between gap-2 pt-4 border-t border-gray-200 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={step === "contact" ? () => onOpenChange(false) : goPrev}
            className="rounded-xl"
            data-testid={step === "contact" ? "checkout-cancel" : "checkout-prev"}
            disabled={submitting}
          >
            {step === "contact" ? "Annuleren" : (<><ChevronLeft className="w-4 h-4 mr-1" />Vorige</>)}
          </Button>
          {step !== "review" ? (
            <Button
              type="button"
              onClick={goNext}
              className="bg-[#1a5193] hover:bg-[#194d8d] text-white rounded-xl font-chivo font-bold uppercase text-xs"
              data-testid="checkout-next"
            >
              Volgende <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submitOrder}
              disabled={submitting}
              className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs"
              data-testid="checkout-pay"
            >
              {submitting ? "Bezig…" : "Betalen via Stripe"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="p-3 bg-gray-50 border border-gray-200 text-sm">
      <div className="text-[11px] uppercase font-bold text-gray-500 mb-1">{title}</div>
      {children}
    </div>
  );
}
